import { useEffect, useRef, useState, useMemo } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Modal, Table, Spin } from "antd";
import "../../../styles/Dashboard.css";
import { EyeOutlined, LoadingOutlined } from "@ant-design/icons";
import newStyled from "@emotion/styled";
import StoreWisePerformance from "./StoreWisePerformance";
import CityWisePerformance from "./CityWisePerformance";
import { CYCLE_COLORS } from "../../../utils/Service/colors";
import CycleBar from "../../../components/common/CycleBar";
import CardHeader from "../../../components/common/CardHeader";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  ChartDataLabels,
);

// Score-range buckets shown on the x-axis (unchanged from original component)
const bucketLabels = ["0 – 20", "21 – 40", "41 – 60", "61 – 80", "81 – 100"];

const MAX_CYCLES = 5;

const Branchperformance = ({
  cycleIds,
  cycles,
  isLoading,
  stData,
  cityData,
  auditStoreIdPass,
  downloading,
  downloadBarChartAsPNG,
  storeTrendsLoading,
  cityTrendsLoading,
  showStorePerformance,
  showCityPerformance,
}) => {
  const [fontSize, setFontSize] = useState(14);
  const [selectedCycleIds, setSelectedCycleIds] = useState([]);

  const [storeBarModal, setStoreBarModal] = useState(null);
  const [cityBarModal, setCityBarModal] = useState(null);

  const barChartRef = useRef(null);

  const cyclePool = useMemo(() => {
    const storeCycles = stData?.data || [];
    const cityCycleMeta = cityData?.data?.[0]?.cycles || [];

    const idOrder = storeCycles.length
      ? storeCycles.map((c) => ({
          id: c.audit_cycle_id,
          name: c.audit_cycle_name,
        }))
      : cityCycleMeta.map((c) => ({
          id: c.audit_cycle_id,
          name: c.audit_cycle_name,
        }));

    const orderedIds = cycleIds?.length
      ? cycleIds
      : idOrder.map((meta) => meta.id);

    return orderedIds
      .map((cycleId, idx) => {
        const meta = idOrder.find((cycle) => cycle.id === cycleId);
        if (!meta) return null;

        const palette = CYCLE_COLORS[idx % CYCLE_COLORS.length];

        const storeCycle = storeCycles.find(
          (c) => c.audit_cycle_id === cycleId,
        );
        const storeCounts = storeCycle
          ? storeCycle.ranges.map((r) => r.store_count || 0)
          : bucketLabels.map(() => 0);

        const cityCounts = cityData?.data?.length
          ? cityData.data.map((bucket) => {
              const match = bucket.cycles.find(
                (c) => c.audit_cycle_id === cycleId,
              );
              return match?.city_count || 0;
            })
          : bucketLabels.map(() => 0);

        return {
          id: meta.id,
          name: meta.name,
          barBorderColor: palette.main,
          color: palette.bg,
          storeCounts,
          cityCounts,
        };
      })
      .filter(Boolean);
  }, [cycleIds, stData, cityData]);

  // useEffect(() => {
  //   if (!cyclePool.length) return;
  //   setSelectedCycleIds((prev) => {
  //     const stillValid = prev.filter((id) =>
  //       cyclePool.some((c) => c.id === id),
  //     );
  //     if (stillValid.length) return stillValid;
  //     return cyclePool.slice(0, MAX_CYCLES).map((c) => c.id);
  //   });
  // }, [cyclePool]);

  useEffect(() => {
    if (!cyclePool.length) return;

    setSelectedCycleIds(cyclePool.slice(0, MAX_CYCLES).map((c) => c.id));
  }, [cyclePool]);

  const resizeHandler = () => {
    if (window.innerWidth < 767) {
      setFontSize(10);
    } else if (window.innerWidth < 991) {
      setFontSize(12);
    } else {
      setFontSize(14);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", resizeHandler);
    resizeHandler();
    return () => window.removeEventListener("resize", resizeHandler);
  }, []);

  const selectedCycles = cyclePool.filter((c) =>
    selectedCycleIds.includes(c.id),
  );
  const selectedStoreCycles = selectedCycles.map((c) => ({
    ...c,
    counts: c.storeCounts,
  }));
  const selectedCityCycles = selectedCycles.map((c) => ({
    ...c,
    counts: c.cityCounts,
  }));

  const handleStoreBarClick = (cycle, rangeIndex) => {
    setStoreBarModal({
      cycleId: cycle.id,
      cycleName: cycle.name,
      rangeIndex,
    });
  };

  const handleCityBarClick = (cycle, rangeIndex) => {
    setCityBarModal({
      cycleId: cycle.id,
      cycleName: cycle.name,
      rangeIndex,
    });
  };

  const closeStoreBarModal = () => setStoreBarModal(null);
  const closeCityBarModal = () => setCityBarModal(null);

  const storeBarModalData = useMemo(() => {
    if (!storeBarModal) return [];
    const cycle = (stData?.data || []).find(
      (c) => c.audit_cycle_id === storeBarModal.cycleId,
    );
    if (!cycle) return [];
    const range = cycle.ranges[storeBarModal.rangeIndex];
    if (!range) return [];
    return range.stores.map((s) => ({
      label: s.store?.name || "Unknown Store",
      value: s.score?.value || 0,
      colorCode: s.score?.color_code || null,
    }));
  }, [stData, storeBarModal]);

  const cityBarModalData = useMemo(() => {
    if (!cityBarModal) return [];
    const bucket = cityData?.data?.[cityBarModal.rangeIndex];
    if (!bucket) return [];
    const match = bucket.cycles.find(
      (c) => c.audit_cycle_id === cityBarModal.cycleId,
    );
    if (!match) return [];
    return match.cities.map((c) => ({
      label: c.city?.name || "Unknown City",
      value: c.score?.value || 0,
      colorCode: c.score?.color_code || null,
    }));
  }, [cityData, cityBarModal]);

  const storeColumns = [
    { title: "Store Name", dataIndex: "label", key: "cityName", width: 200 },
    {
      title: "Score (%)",
      dataIndex: "value",
      key: "npsScore",
      className: "centered-column",
      width: 200,
      render: (value, record) => (
        <span
          style={{
            backgroundColor: "#007dc1",
            color: "#fff",
            padding: "5px 10px",
            borderRadius: "4px",
            textAlign: "center",
          }}
        >
          {value}%
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      className: "centered-column",
      render: (_, record, index) => {
        return (
          <a
            href={"/storebrowser"}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              backgroundColor: "#007dc1",
              padding: "4px 18px",
              display: "inline-block",
              textDecoration: "none",
              borderRadius: "5px",
            }}
          >
            {isLoading ? (
              <Spin
                indicator={<LoadingOutlined spin />}
                size="small"
                style={{ color: "#fff" }}
              />
            ) : (
              <EyeOutlined style={{ color: "#fff" }} />
            )}
          </a>
        );
      },
    },
  ];

  const cityColumns = [
    { title: "City Name", dataIndex: "label", key: "cityName", width: 200 },
    {
      title: "Score (%)",
      dataIndex: "value",
      key: "npsScore",
      className: "centered-column",
      width: 200,
      render: (value, record) => (
        <span
          style={{
            backgroundColor: "#007dc1",
            color: "#fff",
            padding: "5px 10px",
            borderRadius: "4px",
            textAlign: "center",
          }}
        >
          {value}%
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      className: "centered-column",
      render: (_, record, index) => {
        // const auditStoreId = auditStoreIdPass?.[index];
        return (
          <a
            href={"/storebrowser"}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              backgroundColor: "#007dc1",
              padding: "4px 18px",
              display: "inline-block",
              textDecoration: "none",
              borderRadius: "5px",
            }}
          >
            {isLoading ? (
              <Spin
                indicator={<LoadingOutlined spin />}
                size="small"
                style={{ color: "#fff" }}
              />
            ) : (
              <EyeOutlined style={{ color: "#fff" }} />
            )}
          </a>
        );
      },
    },
  ];

  return (
    <CardWrap className="dashboard-card shadow p-3" ref={barChartRef}>
      <CardHeader
        title="Branch Performance Comparison"
        subtitle={`Compare store performance up to ${MAX_CYCLES} audit cycles`}
        downloading={downloading}
        onDownloadClick={() =>
          downloadBarChartAsPNG(barChartRef, "branch-performance")
        }
      />
      <CycleBar cycleIds={cycleIds} cycles={cycles} />

      {showStorePerformance && (
        <StoreWisePerformance
          fontSize={fontSize}
          stLoading={storeTrendsLoading}
          onBarClick={handleStoreBarClick}
          bucketLabels={bucketLabels}
          selectedCycles={selectedStoreCycles}
        />
      )}

      {showCityPerformance && (
        <CityWisePerformance
          fontSize={fontSize}
          cityLoading={cityTrendsLoading}
          onBarClick={handleCityBarClick}
          bucketLabels={bucketLabels}
          selectedCycles={selectedCityCycles}
        />
      )}

      {/* Legend row, matching the screenshot */}
      <div className="d-flex flex-wrap justify-content-center gap-4 mt-2">
        {selectedCycles.map((cycle) => (
          <div key={cycle.id} className="d-flex align-items-center gap-2">
            <Dot color={cycle.barBorderColor} />
            <span
              style={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "#0F2A3D",
              }}
            >
              {cycle.name}
            </span>
          </div>
        ))}
      </div>

      {/* ============================= Modals ====================================*/}
      {showStorePerformance && (
        <ModalStl
          title={
            storeBarModal
              ? `${storeBarModal.cycleName} -   ${bucketLabels[storeBarModal.rangeIndex]}`
              : "Store Data"
          }
          open={!!storeBarModal}
          onCancel={closeStoreBarModal}
          footer={null}
          width={700}
          style={{ maxHeight: "700px !important", overflowY: "auto" }}
        >
          <TableStyle
            dataSource={storeBarModalData}
            columns={storeColumns}
            rowKey={(record, index) => index}
            pagination={false}
            bordered={false}
            rowClassName={() => "custom-row"}
          />
        </ModalStl>
      )}

      {showCityPerformance && (
        <ModalStl
          title={
            cityBarModal
              ? `${cityBarModal.cycleName} — ${bucketLabels[cityBarModal.rangeIndex]}`
              : "City Data"
          }
          open={!!cityBarModal}
          onCancel={closeCityBarModal}
          footer={null}
          width={700}
          style={{ maxHeight: "700px !important", overflowY: "auto" }}
        >
          <TableStyle
            dataSource={cityBarModalData}
            columns={cityColumns}
            rowKey={(record, index) => index}
            pagination={false}
            bordered={false}
            rowClassName={() => "custom-row"}
          />
        </ModalStl>
      )}
    </CardWrap>
  );
};

const CardWrap = newStyled.div`
  min-height: 420px;
  background-color: #FFFFFF;
  border-radius: 10px;
`;

const Dot = newStyled.span`
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: ${(props) => props.color || "#007dc1"};
  flex-shrink: 0;
`;

const TableStyle = newStyled(Table)`
.centered-column {
    text-align: center !important; 
  }

 :where(.css-dev-only-do-not-override-1kf000u).ant-table-wrapper table {
    width: 100%;
    text-align: start;
    border-radius: 8px 8px 0 0;
    border-spacing: 0;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td
   {
    border: none !important;
  }
 :where(.css-dev-only-do-not-override-1kf000u).ant-table-wrapper .ant-table-thead >tr>th, :where(.css-dev-only-do-not-override-1kf000u).ant-table-wrapper .ant-table-thead >tr>td {
    color: #000 !important;
     font-size:1.1rem !important;
   font-family:'Roboto', sans-serif !important;
    background: rgb(242,242,242) !important;
  }

`;
const ModalStl = newStyled(Modal)`
:where(.css-dev-only-do-not-override-1kf000u).ant-modal .ant-modal-title {
color: #003C5D !important;
font-size:1.5rem !important;
font-family:'Roboto', sans-serif !important;
}
`;

export default Branchperformance;
